import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { ROUTES } from '../config/routes';
import type { Test, User } from '../types';

export type TestTypeFilter = 'ALL' | 'CHECKERBOARD' | 'LETTER';

export interface ReportFilters {
  startDate: string;
  endDate: string;
  testType: TestTypeFilter;
}

export const DEFAULT_REPORT_FILTERS: ReportFilters = {
  startDate: '',
  endDate: '',
  testType: 'ALL',
};

interface TestHistoryProps {
  userId?: string;
  showUserInfo?: boolean;
  maxResults?: number;
  paginate?: boolean;
  pageSize?: number;
  className?: string;
  onDataLoaded?: (tests: TestWithUser[]) => void;
  /** Optional server-side status filter (e.g. 'COMPLETED' for the Reports tab). */
  status?: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'ALL';
  /** Controlled report filters (dates + test type). Falls back to internal state when omitted. */
  filters?: ReportFilters;
  onFiltersChange?: (filters: ReportFilters) => void;
}

interface TestWithUser extends Test {
  user?: User | null;
}

// Backend returns Prisma enum values in UPPER CASE, older rows/mocks may use
// lower case — normalize before comparing so badges, trends and detail links
// work regardless of casing.
export const normalizeStatus = (status: unknown) => String(status || '').toUpperCase();
export const isCompletedStatus = (status: unknown) => normalizeStatus(status) === 'COMPLETED';

export const getEmployeeName = (test: TestWithUser): string => {
  if (test.employee?.name) return test.employee.name;
  if (test.employee?.employeeId) return test.employee.employeeId;
  return 'Employee';
};

export const getEmployeeCode = (test: TestWithUser): string => {
  if (test.employee?.employeeId) return test.employee.employeeId;
  return 'N/A';
};

export const formatShift = (shift: unknown) => {
  const value = String(shift || '').toUpperCase();
  if (!value) return '-';
  return value.charAt(0) + value.slice(1).toLowerCase();
};

export const normalizeTestType = (testType: unknown) => String(testType || '').toUpperCase();

export const formatTestType = (testType: unknown) => {
  const value = normalizeTestType(testType);
  if (value === 'LETTER') return 'Letter recall';
  if (value === 'CHECKERBOARD') return 'Checkerboard';
  return '-';
};

const TestHistory: React.FC<TestHistoryProps> = ({
  userId,
  showUserInfo = true,
  maxResults = 10,
  paginate = false,
  pageSize,
  className = '',
  onDataLoaded,
  status,
  filters: filtersProp,
  onFiltersChange
}) => {
  const [tests, setTests] = useState<TestWithUser[]>([]);
  const [filteredData, setFilteredData] = useState<TestWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [internalFilters, setInternalFilters] = useState<ReportFilters>(DEFAULT_REPORT_FILTERS);
  const filters = filtersProp ?? internalFilters;
  const resolvedPageSize = pageSize || 50;

  const updateFilters = (patch: Partial<ReportFilters>) => {
    const next = { ...filters, ...patch };
    if (!filtersProp) {
      setInternalFilters(next);
    }
    onFiltersChange?.(next);
    if (paginate) {
      setPage(1);
    }
  };

  useEffect(() => {
    fetchTestHistory();
  }, [userId, page, filters.startDate, filters.endDate, filters.testType, status]);

  useEffect(() => {
    let filtered = tests;

    if (!paginate) {
      if (filters.startDate) {
        filtered = filtered.filter(test => new Date(test.testDate) >= new Date(filters.startDate));
      }

      if (filters.endDate) {
        filtered = filtered.filter(test => new Date(test.testDate) <= new Date(filters.endDate));
      }
    }

    setFilteredData(filtered);
  }, [tests, filters, paginate]);

  const fetchTestHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      let fetchedTests: TestWithUser[] = [];
      let fetchedTotal: number | null = null;
      if (!userId) {
        const response = await apiService.getAllTests({
          ...(paginate
            ? {
                limit: resolvedPageSize,
                offset: (page - 1) * resolvedPageSize,
                includeCount: true
              }
            : {
                limit: maxResults
              }),
          includeRaw: false,
          ...(status && status !== 'ALL' ? { status } : {}),
          ...(filters.testType !== 'ALL' ? { testType: filters.testType } : {}),
          ...(filters.startDate ? { startDate: `${filters.startDate}T00:00:00` } : {}),
          ...(filters.endDate ? { endDate: `${filters.endDate}T23:59:59` } : {})
        });
        if (response.success && response.data) {
          const payload = response.data as any;
          if (Array.isArray(payload)) {
            fetchedTests = payload;
          } else {
            fetchedTests = payload.tests || [];
            fetchedTotal = typeof payload.total === 'number' ? payload.total : null;
          }
        }
      } else {
        const response = await apiService.getUserTests(userId, {
          ...(paginate
            ? {
                limit: resolvedPageSize,
                offset: (page - 1) * resolvedPageSize,
                includeCount: true
              }
            : {
                limit: maxResults
              }),
          includeRaw: false,
          ...(filters.testType !== 'ALL' ? { testType: filters.testType } : {}),
          ...(filters.startDate ? { startDate: `${filters.startDate}T00:00:00` } : {}),
          ...(filters.endDate ? { endDate: `${filters.endDate}T23:59:59` } : {})
        });
        if (response.success && response.data) {
          const payload = response.data as any;
          if (Array.isArray(payload)) {
            fetchedTests = payload;
          } else {
            fetchedTests = payload.tests || [];
            fetchedTotal = typeof payload.total === 'number' ? payload.total : null;
          }
          // getUserTests has no server-side status filter — apply client-side.
          if (status && status !== 'ALL') {
            fetchedTests = fetchedTests.filter(
              (test) => normalizeStatus((test as TestWithUser).status) === status
            );
          }
        }
      }
      setTests(fetchedTests);
      setTotalCount(fetchedTotal);
      if (onDataLoaded) {
        onDataLoaded(fetchedTests);
      }
    } catch (err) {
      console.warn('Failed to load test history:', err);
      setTests([]);
      setError(err instanceof Error ? err.message : 'Failed to load test history.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    updateFilters(field === 'startDate' ? { startDate: value } : { endDate: value });
  };

  const handleTestTypeChange = (value: string) => {
    updateFilters({ testType: value as TestTypeFilter });
  };

  const clearDateRange = () => {
    updateFilters({ ...DEFAULT_REPORT_FILTERS });
  };

  const getPerformanceTrend = () => {
    const completedTests = filteredData.filter(test => test.results && isCompletedStatus(test.status));
    if (completedTests.length < 2) return null;

    const recentTests = completedTests.slice(0, 5);
    const olderTests = completedTests.slice(5, 10);

    if (olderTests.length === 0) return null;

    const recentAvg = recentTests.reduce((sum, test) => sum + (test.results?.averageReactionTime || 0), 0) / recentTests.length;
    const olderAvg = olderTests.reduce((sum, test) => sum + (test.results?.averageReactionTime || 0), 0) / olderTests.length;

    const improvement = olderAvg - recentAvg;
    const improvementPercent = (improvement / olderAvg) * 100;

    return {
      improvement,
      improvementPercent,
      isImproving: improvement > 0,
      recentAvg,
      olderAvg
    };
  };

  const trend = getPerformanceTrend();

  const hasLetterResults = filteredData.some((test) => normalizeTestType(test.testType) === 'LETTER');

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500 mx-auto mb-2"></div>
          <p className="text-navy-200">Loading test history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-navy-800 shadow rounded-lg p-6 ${className}`}>
        <div className="text-center text-red-400">
          <svg className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="font-medium">Error Loading Test History</p>
          <p className="text-sm text-navy-200 mt-1">{error}</p>
          <button
            onClick={fetchTestHistory}
            className="mt-3 bg-gold-500 hover:bg-gold-600 text-navy-950 font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Filters */}
      <div className="bg-navy-800 shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="mb-4 sm:mb-0">
            <h3 className="text-lg font-medium text-white">Test History</h3>
            <p className="text-sm text-navy-300">
              {paginate && typeof totalCount === 'number'
                ? `${totalCount} test${totalCount !== 1 ? 's' : ''} found`
                : `${filteredData.length} test${filteredData.length !== 1 ? 's' : ''} found`}
            </p>
          </div>

          {/* Date Range + Test Type Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center space-x-2">
              <label htmlFor="startDate" className="text-sm text-navy-100">From:</label>
              <input
                type="date"
                id="startDate"
                value={filters.startDate}
                max={filters.endDate || undefined}
                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                className="[color-scheme:dark] bg-navy-900 text-white border border-navy-700 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="endDate" className="text-sm text-navy-100">To:</label>
              <input
                type="date"
                id="endDate"
                value={filters.endDate}
                min={filters.startDate || undefined}
                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                className="[color-scheme:dark] bg-navy-900 text-white border border-navy-700 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="testType" className="text-sm text-navy-100">Test type:</label>
              <select
                id="testType"
                value={filters.testType}
                onChange={(e) => handleTestTypeChange(e.target.value)}
                className="bg-navy-900 text-white border border-navy-700 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              >
                <option value="ALL">All types</option>
                <option value="CHECKERBOARD">Checkerboard</option>
                <option value="LETTER">Letter recall</option>
              </select>
            </div>
            {(filters.startDate || filters.endDate || filters.testType !== 'ALL') && (
              <button
                onClick={clearDateRange}
                className="text-sm text-navy-200 hover:text-white underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Performance Trend */}
        {trend && (
          <div className="mt-4 p-4 bg-navy-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-2">Performance Trend</h4>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${trend.isImproving ? 'text-green-400' : 'text-red-400'}`}>
                {trend.isImproving ? (
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {trend.isImproving ? 'Improving' : 'Declining'}
                </span>
              </div>
              <div className="text-sm text-navy-200">
                {Math.abs(trend.improvementPercent).toFixed(1)}% change
                ({Math.abs(trend.improvement).toFixed(0)}ms)
              </div>
              <div className="text-sm text-navy-300">
                Recent avg: {Math.ceil(trend.recentAvg)}ms vs Previous: {Math.ceil(trend.olderAvg)}ms
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test List */}
      {filteredData.length === 0 ? (
        <div className="bg-navy-800 shadow rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-navy-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Tests Found</h3>
          <p className="text-navy-200">No test history matches your current filters.</p>
        </div>
      ) : (
        <div className="bg-navy-800 shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Recent Tests</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-navy-700">
                <thead className="bg-navy-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Date
                    </th>
                    {showUserInfo && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                        Employee
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Shift
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Test Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Avg Reaction Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Med Reaction Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Lapses
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      False Starts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Accuracy
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-navy-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-navy-800 divide-y divide-navy-700">
                  {filteredData.map((test) => {
                    const completed = isCompletedStatus(test.status);
                    return (
                    <tr key={test.id} className="hover:bg-navy-900">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        <div>
                          <div className="font-medium">
                            {new Date(test.testDate).toLocaleDateString()}
                          </div>
                          <div className="text-navy-300">
                            {new Date(test.testDate).toLocaleTimeString()}
                          </div>
                        </div>
                      </td>
                      {showUserInfo && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          <div>
                            <div className="font-medium">{getEmployeeName(test)}</div>
                            <div className="text-navy-300">{getEmployeeCode(test)}</div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {(test as TestWithUser).department || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatShift((test as TestWithUser).workShift)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {formatTestType((test as TestWithUser).testType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          completed
                            ? 'bg-green-500/15 text-green-400'
                            : normalizeStatus(test.status) === 'PENDING'
                            ? 'bg-yellow-500/15 text-yellow-400'
                            : 'bg-red-500/15 text-red-400'
                        }`}>
                          {normalizeStatus(test.status).toLowerCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {test.results ? `${Math.round(test.results.averageReactionTime)} ms` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {test.results?.medianReactionTime != null ? `${Math.round(test.results.medianReactionTime)} ms` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {test.results ? test.results.totalLapses : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {test.results ? test.results.totalFalseStarts : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {test.results?.accuracy != null ? `${test.results.accuracy.toFixed(1)}%` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {completed && (
                          <Link
                            to={ROUTES.ADMIN.RESULTS(test.id)}
                            className="text-gold-400 hover:text-gold-300"
                          >
                            View Details
                          </Link>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasLetterResults && (
              <p className="mt-4 text-xs text-navy-300">
                Letter recall: major lapses include unanswered targets.
              </p>
            )}

            {paginate && typeof totalCount === 'number' && totalCount > resolvedPageSize && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-navy-200">
                  Page {page} of {Math.ceil(totalCount / resolvedPageSize)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm rounded border border-navy-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-navy-900"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={page >= Math.ceil(totalCount / resolvedPageSize)}
                    className="px-3 py-1 text-sm rounded border border-navy-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-navy-900"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestHistory;
